import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AdsApi, { EXPORTS } from '../api/ads';
import {
  AdAccountFilter, AdSetFilter, CampaignFilter, DEFAULT_SITE, SiteFilter,
} from '../components/ads/AdFilters';
import { DateRangeFilter, FilterBar } from '../components/filters/Filters';
import { METRICS } from '../components/insights/metrics';
import StatTile from '../components/insights/StatTile';
import TopAds from '../components/insights/TopAds';
import TrendChart from '../components/insights/TrendChart';
import DownloadMenu from '../components/table/DownloadMenu';
import useUrlQuery, { scopeParams } from '../hooks/useUrlQuery';
import { formatDay } from '../utils/dates';

const DEFAULTS = { site: DEFAULT_SITE, metric: 'all', chart: 'line', unit: 'day' };
// filters that narrow the data; metric, chart and unit only pick what the chart shows
const FILTER_KEYS = ['ad_account', 'campaign', 'adset', 'range', 'since', 'until'];

export default function Insights() {
  const [query, setQuery] = useUrlQuery(DEFAULTS);
  const params = useMemo(() => scopeParams(query), [query]);
  const paramsKey = JSON.stringify(params);
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    let current = true;
    setState((previous) => ({ ...previous, loading: true, error: null }));
    AdsApi.insights(JSON.parse(paramsKey))
      .then((data) => current && setState({ data, loading: false, error: null }))
      .catch((e) => current && setState((previous) => ({ ...previous, loading: false, error: e.message })));
    return () => {
      current = false;
    };
  }, [paramsKey]);

  const { data, loading, error } = state;
  const currency = data?.currencies.length === 1 ? data.currencies[0] : null;
  const delivered = data && (data.totals.impressions > 0 || Number(data.totals.spend) > 0);
  const active = FILTER_KEYS.some((key) => query[key]);

  return (
    <Box component="section">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
             alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>Insights</Typography>
          <Typography color="text.secondary">
            {data ? `${formatDay(params.since)} – ${formatDay(params.until)}, compared with `
              + `${formatDay(data.previous.since)} – ${formatDay(data.previous.until)}`
              : 'Delivery and spend of your synced ad accounts.'}
          </Typography>
        </Box>
        <DownloadMenu path={EXPORTS.insights} params={params} disabled={!delivered} label="Ad level daily rows"
                      text="Download daily rows" />
      </Stack>

      <FilterBar active={active} date={<DateRangeFilter query={query} onChange={setQuery} />}
                 onClear={() => setQuery(Object.fromEntries(FILTER_KEYS.map((key) => [key, null])))}>
        <SiteFilter query={query} setQuery={setQuery} />
        <AdAccountFilter query={query} setQuery={setQuery} />
        <CampaignFilter query={query} setQuery={setQuery} />
        <AdSetFilter query={query} setQuery={setQuery} />
      </FilterBar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data?.currencies.length > 1 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Spend adds up ad accounts in {data.currencies.join(', ')}. Pick one ad account to compare spend in one
          currency.
        </Alert>
      )}

      {!data ? <LoadingFrame /> : (
        // refetches keep the last result on screen, dimmed
        <Stack spacing={2} sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }} aria-busy={loading}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
            {METRICS.map((metric) => (
              <StatTile key={metric.id} metric={metric} totals={data.totals} previous={data.previous.totals}
                        currency={currency} previousLabel={`${formatDay(data.previous.since)} – `
                          + `${formatDay(data.previous.until)}`} />
            ))}
          </Box>
          {delivered ? (
            <>
              <TrendChart daily={data.daily} currency={currency} metricId={query.metric}
                          onMetricChange={(metric) => setQuery({ metric })} chartType={query.chart}
                          onChartTypeChange={(chart) => setQuery({ chart })} unit={query.unit}
                          onUnitChange={(unit) => setQuery({ unit })} params={params} />
              <TopAds ads={data.top_ads} query={query} params={params} />
            </>
          ) : <NoDelivery since={params.since} until={params.until} filtered={active} />}
        </Stack>
      )}
    </Box>
  );
}

function NoDelivery({ since, until, filtered }) {
  return (
    <Paper variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
      <InsightsOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      <Typography sx={{ fontWeight: 600, mt: 1 }}>
        No delivery between {formatDay(since)} and {formatDay(until)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mt: 0.5 }}>
        Insights come from the daily sync of enabled ad accounts.
        {filtered ? ' Try a longer date range or clear the filters.' : ' Try a longer date range, or check that'
          + ' syncing is on for your ad accounts.'}
      </Typography>
      <Button component={RouterLink} to="/ad-accounts" sx={{ mt: 2 }}>Go to ad accounts</Button>
    </Paper>
  );
}

function LoadingFrame() {
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
        {METRICS.map((metric) => <Skeleton key={metric.id} variant="rounded" height={104} />)}
      </Box>
      <Skeleton variant="rounded" height={380} />
    </Stack>
  );
}
